import React from 'react';

import { PHOTOS } from '../lib/art';

/**
 * A declared photographic slot.
 *
 * Renders the real photograph once one exists, and a designed placeholder
 * stating the art direction until then. The aspect-ratio box is reserved in
 * both states, so dropping the file in later causes no layout shift and no
 * cumulative-layout-shift penalty.
 *
 * `priority` marks the one image above the fold: it loads eagerly and gets a
 * high fetch priority. Everything else is lazy and async-decoded.
 */
export function Photo({
  name,
  className = '',
  priority = false,
  zoom = true,
  quiet = false,
  flush = false,
}) {
  const spec = PHOTOS[name];

  if (!spec) {
    // A typo in a slot name should be loud in development and invisible in
    // production, never a silently missing section.
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(`Photo: unknown slot "${name}"`);
    }
    return null;
  }

  const classes = [
    'photo',
    zoom && spec.ready ? 'photo--zoom' : '',
    quiet ? 'photo--quiet' : '',
    flush ? 'photo--flush' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} style={{ aspectRatio: spec.ratio }}>
      {spec.ready ? (
        <img
          src={spec.src}
          width={spec.width}
          height={spec.height}
          alt={spec.alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchpriority={priority ? 'high' : undefined}
        />
      ) : (
        // Not decorative: it stands in for real content, so it is announced.
        <div
          className="photo__slot"
          role={quiet ? 'presentation' : 'img'}
          aria-label={quiet ? undefined : spec.alt}
          aria-hidden={quiet ? 'true' : undefined}
        >
          <span className="photo__slot-name">{spec.slot}</span>
          <span className="photo__slot-desc">{spec.brief}</span>
        </div>
      )}
    </div>
  );
}

export default Photo;
